import {HttpClient} from '@angular/common/http';
import {Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {select, Store} from '@ngrx/store';
import {DateTime} from 'luxon';
import {
  addonScopesFromContext,
  ContextData,
  RequestDetails as ContextRequestDetails,
} from '../../../common/context-data';
import {
  CartItem as ClareCartItem,
  CartItemMetadata,
  CartItemOrder,
  CartItemQuote,
  CartItemReferences,
  CartItemSeCommerceApi,
} from '@sportngin/se-commerce-sdk/models/clare';
import {RequestDetailsDto} from '@sportngin/se-commerce-sdk';
import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {DateService} from '../../../common/service/core';
import * as contextSelectors from '../../../common/context-data/context-data.selectors';
import {selectCurrentUserUuid} from '../../../common/current-user/current-user.selectors';
import {
  CartItem,
  CartItemInterface,
  Image,
  isDisplayableImageSrc,
  participantDtoToPersona,
  personaToParticipantDto,
  personaToRegistrantDto,
  ProductReference,
  Quote,
  QuoteParams,
  Reference,
  registrantDtoToPersona,
} from '../../model';
import {ApiRequestOptions, CommerceApiService} from './commerce-api.service';

@Injectable({
  providedIn: 'root',
})
export class CartItemApiService extends CommerceApiService {
  readonly controllerPath = 'v3/commerce/shopper/cart';

  protected contextData: Signal<ContextData>;
  protected currentUserUuid: Signal<string | undefined>;

  constructor(
    protected http: HttpClient,
    protected store: Store,
  ) {
    super(http, store);

    this.contextData = toSignal(
      this.store.pipe(
        select(contextSelectors.selectContextData),
        filter(cd => !!cd),
      ),
    ) as Signal<ContextData>;
    this.currentUserUuid = toSignal(this.store.select(selectCurrentUserUuid));
  }

  get guestKey(): string {
    return this.contextData().guestKey;
  }

  list(context: ContextData): Observable<CartItem[]> {
    const url = this.getUrl('/items/addons');
    return this.POST<ClareCartItem[]>(
      url,
      addonScopesFromContext(context),
      this.getOptions({
        params: {guestKey: context.guestKey},
      }),
    ).pipe(
      map((cartItems: ClareCartItem[]) => {
        return cartItems.map(clareCartItemToCartItem);
      }),
    );
  }

  add(cartItem: CartItemInterface): Observable<CartItem> {
    const url = this.getUrl('/items');
    const dto = cartItemToClareCartItem(
      cartItem,
      this.currentUserUuid(),
      this.contextData()?.bossOrganization?.name,
    );

    return this.POST<ClareCartItem>(url, dto, this.getOptions()).pipe(
      map(clareCartItemToCartItem),
    );
  }

  edit(cartItem: CartItem): Observable<CartItem> {
    const url = this.getUrl(`/items/${cartItem.id}`);
    const dto = cartItemToClareCartItem(
      cartItem,
      this.currentUserUuid(),
      this.contextData()?.bossOrganization?.name,
    );

    return this.PATCH<ClareCartItem>(url, dto, this.getOptions()).pipe(
      map(clareCartItemToCartItem),
    );
  }

  remove(cartItemId: string): Observable<void> {
    const url = this.getUrl(`/items/${cartItemId}`);
    return this.DELETE(url, this.getOptions());
  }

  removeBatch(itemIds: string[]): Observable<void> {
    const url = this.getUrl('/items/remove');
    return this.POST<void>(url, {itemIds}, this.getOptions());
  }

  override getUrl(path = ''): string {
    return super.getUrl(`${this.controllerPath}${path}`);
  }

  protected override getOptions(
    src?: Partial<ApiRequestOptions>,
  ): ApiRequestOptions {
    const options: ApiRequestOptions = {
      params: {guestKey: this.guestKey},
      ...src,
    };

    if (!this.seApiToken()) {
      options.withCredentials = true;
    }

    return super.getOptions(options);
  }
}

function cartItemToClareCartItem(
  source: CartItemInterface,
  currentUserUuid?: string,
  hostOrg?: string,
): ClareCartItem {
  const references = productReferenceToClareReferences(source.references);
  const participant = references?.participant;
  const cartMetadata = isRecord(source.cartMetadata)
    ? ({...source.cartMetadata} as Partial<CartItemMetadata>)
    : {};

  const order = compactUndefined({
    ...(cartMetadata.order ?? {}),
    description:
      cartItemOrderDescription(source.fulfilledBy, hostOrg) ??
      cartMetadata.order?.description,
    user_uuid: currentUserUuid ?? cartMetadata.order?.user_uuid,
  }) as CartItemOrder;

  const seCommerceApi = compactUndefined({
    ...(cartMetadata.SeCommerceApi ?? {}),
    ...cartItemSeCommerceApi(source),
  }) as CartItemSeCommerceApi;

  const metadata = compactUndefined({
    ...cartMetadata,
    order: hasValues(order) ? order : undefined,
    references: references ?? cartMetadata.references,
    for:
      cartMetadata.for ??
      (participant?.name ? {title: participant.name} : undefined),
    SeCommerceApi: hasValues(seCommerceApi) ? seCommerceApi : undefined,
  }) as CartItemMetadata;

  return compactUndefined({
    quantity: source.quantity,
    price:
      source.unitPrice !== undefined ? String(source.unitPrice) : undefined,
    name: source.name,
    item_variation_id: source.itemVariationId,
    bundle_id: source.bundleId,
    metadata: hasValues(metadata) ? metadata : undefined,
    quote: source.quote ? quoteToClareCartItemQuote(source.quote) : undefined,
  }) as ClareCartItem;
}

function cartItemOrderDescription(
  supplierName?: string,
  hostOrg?: string,
): string | undefined {
  if (!supplierName || !hostOrg) return undefined;

  return `${supplierName} order placed through ${hostOrg}`;
}

function clareCartItemToCartItem(source: ClareCartItem): CartItem {
  const productName = source.item?.name || source.name;
  const isBundleComponent =
    source.bundle_id !== undefined && source.bundle_id !== null;

  return new CartItem({
    id:
      source.id !== undefined
        ? String(source.id)
        : (source.metadata?.SeCommerceApi?.line_item_id ?? ''),
    productId: source.item?.id !== undefined ? String(source.item.id) : '',
    quantity: source.quantity,
    name: isBundleComponent
      ? productName
      : source.presentation_snapshot?.name || productName,
    image: imageFromClareCartItem(source),
    presentationSnapshot: source.presentation_snapshot,
    unitPrice: unitPriceFromClareCartItem(source),
    quote: source.quote ? clareCartItemQuoteToQuote(source.quote) : undefined,
    references: clareReferencesToProductReference(source.metadata?.references),
    templateKey: source.item?.display_metadata?.template_key ?? '',
    itemVariationId: source.item_variation_id ?? source.item_variation?.id,
    variant: source.item_variation,
    fulfilledBy: source.item?.display_metadata?.fulfilled_by,
    isPromotionalItem: source.is_promo_item ?? false,
    bundleId: finiteBundleId(source.bundle_id),
    bundle_instance_id: source.metadata?.SeCommerceApi?.bundle_instance_id,
    bundle_name: source.metadata?.SeCommerceApi?.bundle_name,
    cartMetadata: source.metadata ? {...source.metadata} : undefined,
  });
}

function productReferenceToClareReferences(
  source?: ProductReference,
): CartItemReferences | undefined {
  if (!source) return undefined;

  const result: CartItemReferences = {};
  if (source.program) {
    result.program = {...source.program};
  }
  if (source.participant) {
    result.participant = {...source.participant};
  }
  if (source.registration) {
    result.registration = {...source.registration};
  }

  return hasValues(result) ? result : undefined;
}

function finiteBundleId(source: number | null | undefined): number | undefined {
  if (source === null || source === undefined) return undefined;

  const bundleId = Number(source);
  return Number.isFinite(bundleId) ? bundleId : undefined;
}

function clareReferencesToProductReference(
  source?: CartItemReferences,
): ProductReference | undefined {
  if (!source) return undefined;

  const result = new ProductReference();
  if (source.program) {
    result.program = new Reference({...source.program});
  }
  if (source.participant) {
    result.participant = new Reference({...source.participant});
  }
  if (source.registration) {
    result.registration = new Reference({...source.registration});
  }

  return result;
}

function cartItemSeCommerceApi(
  source: CartItemInterface,
): CartItemSeCommerceApi {
  return {
    product_id: source.productId,
    line_item_id: source.id,
    survey_participant_id:
      source.references?.participant?.id !== undefined
        ? String(source.references.participant.id)
        : undefined,
    survey_participant_dob: formatReferenceDob(
      source.references?.participant?.dob,
    ),
    survey_participant_type: source.references?.participant?.type,
    survey_participant_gender: source.references?.participant?.gender,
    bundle_instance_id: source.bundle_instance_id,
    bundle_name: source.bundle_name,
  };
}

function quoteToClareCartItemQuote(source: Quote): CartItemQuote {
  return {
    id: source.id,
    provider: source.provider,
    cost_cents: Math.round(source.cost * 100),
    request_details: {
      registrant: personaToRegistrantDto(source.request.registrant),
      participant: personaToParticipantDto(source.request.participant),
      requestDetails: {
        ...source.request.requestDetails,
      },
    },
    display_overrides: source.displayOverrides
      ? {...source.displayOverrides}
      : undefined,
  };
}

function clareCartItemQuoteToQuote(source: CartItemQuote): Quote | undefined {
  if (!source.request_details) return undefined;

  return new Quote({
    id: source.id !== undefined ? String(source.id) : '',
    provider: source.provider ?? '',
    cost: source.cost_cents !== undefined ? Number(source.cost_cents) / 100 : 0,
    request: new QuoteParams({
      registrant: registrantDtoToPersona(source.request_details.registrant),
      participant: participantDtoToPersona(source.request_details.participant),
      requestDetails: clareRequestDetailsToContextRequestDetails(
        source.request_details.requestDetails,
      ),
      provider: source.provider ?? '',
    }),
    displayOverrides: source.display_overrides
      ? {...source.display_overrides}
      : undefined,
  });
}

function clareRequestDetailsToContextRequestDetails(
  source: RequestDetailsDto,
): ContextRequestDetails {
  return {
    eventType: source.eventType,
    eventCost: source.eventCost,
    customerPaysFee: source.customerPaysFee,
    programStartDate: parseDate(source.programStartDate),
    programEndDate: parseDate(source.programEndDate),
    paymentDate: parseDate(source.paymentDate),
    sport: source.sport,
    submittedBy: source.submittedBy,
    orgName: source.orgName,
    eventName: source.eventName,
    recommended: source.recommended,
  };
}

function parseDate(source: Date | string | null | undefined): Date | null {
  return DateService.parseContextDate(source ?? null);
}

function formatReferenceDob(source?: string | Date): string | undefined {
  if (!source) return undefined;
  if (typeof source === 'string') return source;

  return DateTime.fromJSDate(source).toFormat('MM/dd/yyyy');
}

function unitPriceFromClareCartItem(source: ClareCartItem): number {
  const raw =
    source.price ??
    source.amount ??
    (source.item_variation?.price !== undefined
      ? String(source.item_variation.price)
      : undefined);

  if (raw !== undefined) {
    const parsed = Number(raw);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (source.quote?.cost_cents !== undefined) {
    return source.quote.cost_cents / 100;
  }

  return 0;
}

function imageFromClareCartItem(source: ClareCartItem): Image | undefined {
  const snapshot = source.presentation_snapshot;
  const image =
    snapshot?.images.find(image => isDisplayableImageSrc(image.src)) ??
    snapshot?.logo ??
    source.item?.display_metadata?.images?.[0] ??
    source.item?.display_metadata?.logo;

  if (image) {
    return {...image};
  }

  const variationImage = source.item_variation?.image;
  if (
    typeof variationImage === 'string' &&
    isDisplayableImageSrc(variationImage)
  ) {
    return {
      src: variationImage,
      alt: source.item?.name ?? source.name ?? '',
    };
  }

  return undefined;
}

function hasValues(source: object): boolean {
  return Object.values(source).some(value => value !== undefined);
}

function compactUndefined(source: unknown): unknown {
  if (source instanceof Date) {
    return source;
  }

  if (Array.isArray(source)) {
    return source.map(item => compactUndefined(item));
  }

  if (!isRecord(source)) {
    return source;
  }

  return Object.fromEntries(
    Object.entries(source)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, compactUndefined(value)]),
  );
}

function isRecord(source: unknown): source is Record<string, unknown> {
  return !!source && typeof source === 'object' && !Array.isArray(source);
}
